import { useMemo, useState } from 'react';
import { PanelToolbar } from '../../components/panels/PanelToolbar';
import { SkeletonLog } from '../../components/ui/Skeleton';
import { useRtFeed } from '../../lib/rt/feed';
import type { RtChannelKey } from '../../lib/types';
import { StripLog } from './StripLog';
import type { StripTrack } from './StripLog';
import { FeedBadge, FeedMissing } from './FeedState';
import { ACTIVITY_COLORS, hhmm, stamp, timeGrid, timeRows } from './rtFormat';

/** Live channel readings, two to a row, as on the RT Time Log screen. */
const READOUTS: { label: string; key: RtChannelKey; unit: string; decimals: number }[] = [
  { label: 'TVD', key: 'tvd', unit: 'm', decimals: 2 },
  { label: 'Lag Depth', key: 'lagDepth', unit: 'm', decimals: 2 },
  { label: 'Lag Time', key: 'lagTime', unit: 'min', decimals: 1 },
  { label: 'Block Position', key: 'blockPos', unit: 'm', decimals: 2 },
  { label: 'Hookload', key: 'hookload', unit: 'klb', decimals: 1 },
  { label: 'WOB', key: 'wob', unit: 'klb', decimals: 1 },
  { label: 'ROP', key: 'rop', unit: 'm/h', decimals: 1 },
  { label: 'RPM', key: 'rpm', unit: 'rpm', decimals: 0 },
  { label: 'Torque', key: 'torque', unit: 'klbf-ft', decimals: 2 },
  { label: 'SPP', key: 'spp', unit: 'psi', decimals: 0 },
  { label: 'Mud Flow In', key: 'flowIn', unit: 'gpm', decimals: 0 },
  { label: 'Mud Flow Out', key: 'flowOut', unit: '%', decimals: 1 },
  { label: 'Mud Density In', key: 'densIn', unit: 'ppg', decimals: 2 },
  { label: 'Mud Density Out', key: 'densOut', unit: 'ppg', decimals: 2 },
  { label: 'ECD', key: 'ecd', unit: 'ppg', decimals: 2 },
  { label: 'Mud Temperature In', key: 'tempIn', unit: 'degC', decimals: 1 },
  { label: 'Mud Temperature Out', key: 'tempOut', unit: 'degC', decimals: 1 },
  { label: 'Pit Active', key: 'pitActive', unit: 'bbl', decimals: 1 },
  { label: 'Gain/Loss', key: 'gainLoss', unit: 'bbl', decimals: 1 },
  { label: 'Trip Tank', key: 'tripTank', unit: 'bbl', decimals: 1 },
  { label: 'Total Gas', key: 'totalGas', unit: '%', decimals: 2 },
  { label: 'Choke Pressure', key: 'choke', unit: 'psi', decimals: 0 },
];

const SPANS = [30, 60, 120, 360];

const TRACKS: StripTrack[] = [
  { kind: 'axis', width: 46, title: 'Time' },
  { kind: 'activity', width: 22, title: 'Activity' },
  {
    kind: 'curves',
    width: 150,
    title: 'ROP',
    curves: [
      { key: 'rop', label: 'ROP', unit: 'm/h', color: '#2e7d32', min: 100, max: 0 },
      { key: 'blockPos', label: 'Block Position', unit: 'm', color: '#1565c0', min: 0, max: 50 },
      { key: 'hookload', label: 'Hookload', unit: 'klb', color: '#6a1b9a', min: 0, max: 300 },
      { key: 'wob', label: 'WOB', unit: 'klb', color: '#c62828', min: 0, max: 50, dashed: true },
    ],
  },
  { kind: 'value', width: 34, title: 'Hole Depth', key: 'holeDepth', decimals: 1 },
  { kind: 'value', width: 34, title: 'Bit Depth', key: 'bitDepth', decimals: 1 },
  {
    kind: 'curves',
    width: 140,
    title: 'Torque',
    curves: [
      { key: 'torque', label: 'Torque', unit: 'klbf-ft', color: '#ef6c00', min: 0, max: 30 },
      { key: 'rpm', label: 'RPM', unit: 'rpm', color: '#00838f', min: 0, max: 300 },
    ],
  },
  {
    kind: 'curves',
    width: 150,
    title: 'Pressure & Flow',
    curves: [
      { key: 'spp', label: 'SPP', unit: 'psi', color: '#c62828', min: 0, max: 5000 },
      { key: 'flowIn', label: 'Mud Flow In', unit: 'gpm', color: '#1565c0', min: 0, max: 1200 },
      { key: 'flowOut', label: 'Mud Flow Out', unit: '%', color: '#2e7d32', min: 0, max: 100, dashed: true },
    ],
  },
  { kind: 'value', width: 34, title: 'Lag Depth', key: 'lagDepth', decimals: 1 },
  {
    kind: 'curves',
    width: 130,
    title: 'Total Gas',
    curves: [
      { key: 'totalGas', label: 'Total Gas', unit: '%', color: '#d32f2f', min: 0, max: 5 },
      { key: 'pitActive', label: 'Pit Active', unit: 'bbl', color: '#5d4037', min: 800, max: 1100 },
      { key: 'gainLoss', label: 'Gain/Loss', unit: 'bbl', color: '#ad1457', min: -30, max: 30, dashed: true },
    ],
  },
  {
    kind: 'curves',
    width: 130,
    title: 'Mud',
    curves: [
      { key: 'tempIn', label: 'Temp In', unit: 'degC', color: '#1565c0', min: 0, max: 100 },
      { key: 'tempOut', label: 'Temp Out', unit: 'degC', color: '#c62828', min: 0, max: 100, dashed: true },
      { key: 'densIn', label: 'Density In', unit: 'ppg', color: '#2e7d32', min: 8, max: 16 },
      { key: 'ecd', label: 'ECD', unit: 'ppg', color: '#6a1b9a', min: 8, max: 16, dashed: true },
    ],
  },
];

export function RtTimeLog({ wellId }: { wellId: number }) {
  const feed = useRtFeed(wellId);
  const [span, setSpan] = useState(60);
  const rows = useMemo(() => timeRows(feed.samples), [feed.samples]);

  if (feed.status === 'none' || feed.status === 'error') {
    return (
      <section className="panel panel--full" aria-label="RT Timelog">
        <PanelToolbar title="RT Timelog" accent />
        <FeedMissing feed={feed} />
      </section>
    );
  }

  const latest = feed.latest;
  const top = latest ? Date.parse(latest.t) + 15_000 : 0;
  const { minor, major } = timeGrid(span);
  const step = (feed.info?.stepSeconds ?? 5) * 1000;

  return (
    <div className="splitpane splitpane--readouts">
      <section className="readoutgrid" aria-label="Live channel readings">
        {READOUTS.map((r) => (
          <div className="readout-tile" key={r.label}>
            <span className="readout-tile-label">{r.label}</span>
            <span className="readout-tile-stamp">{stamp(latest?.t)}</span>
            <span className="readout-tile-value">{latest ? latest[r.key].toFixed(r.decimals) : '—'}</span>
            <span className="readout-tile-unit">{r.unit}</span>
          </div>
        ))}
      </section>

      <section className="panel" aria-label="RT Timelog">
        <PanelToolbar title="RT Timelog" accent />
        <FeedBadge feed={feed} />

        <div className="activitychips">
          {Object.entries(ACTIVITY_COLORS).map(([label, color]) => (
            <span
              className={latest?.activity === label ? 'chip is-current' : 'chip'}
              key={label}
              style={{ background: color }}
            >
              {label}
            </span>
          ))}
        </div>

        <div className="rtspan">
          Window
          {SPANS.map((m) => (
            <button
              key={m}
              type="button"
              className={m === span ? 'treetool is-active' : 'treetool'}
              aria-pressed={m === span}
              onClick={() => setSpan(m)}
            >
              {m < 60 ? `${m} min` : `${m / 60} h`}
            </button>
          ))}
        </div>

        {feed.status === 'connecting' && !latest ? (
          <SkeletonLog label="Connecting to the real-time feed" />
        ) : (
          <StripLog
            rows={rows}
            tracks={TRACKS}
            top={top}
            span={span * 60_000}
            minor={minor}
            major={major}
            formatY={hhmm}
            gap={step * 3}
          />
        )}
      </section>
    </div>
  );
}
